type _PathParam<Path extends string> = Path extends `${infer L}/${infer R}`
  ? _PathParam<L> | _PathParam<R>
  : Path extends `:${infer Param}`
    ? Param extends `${infer Optional}?`
      ? Optional
      : Param
    : never;
/**
 * Examples:
 * "/a/b/*" -> "*"
 * ":a" -> "a"
 * "/a/:b" -> "b"
 * "/a/blahblahblah:b" -> "b"
 * "/:a/:b" -> "a" | "b"
 * "/:a/b/:c/*" -> "a" | "c" | "*"
 */
export type PathParam<Path extends string> = Path extends "*" | "/*"
  ? "*"
  : Path extends `${infer Rest}/*`
    ? "*" | _PathParam<Rest>
    : _PathParam<Path>;

export const route = <Path extends Route>(
  url: Path,
  propsParams: {
    [key in PathParam<Path>]: string | null;
  },
): string => {
  let newUrl: string = url;
  Object.keys(propsParams).forEach((param) => {
    newUrl = newUrl.replace(`:${param}`, String(propsParams[param as PathParam<Path>]));
  });
  return newUrl;
};

export const routeWithParams = <Path extends Route>(
  url: Path,
  propsParams: {
    [key in PathParam<Path>]: string | null;
  } & Record<string, string | number | boolean | null | undefined>,
): string => {
  // 1) Replace dynamic path params like ":id"
  let newUrl: string = url;
  const consumed = new Set<string>();

  Object.keys(propsParams).forEach((param) => {
    const placeholder = `:${param}`;
    if (newUrl.includes(placeholder)) {
      newUrl = newUrl.replace(
        placeholder,
        encodeURIComponent(String(propsParams[param as PathParam<Path>] ?? "")),
      );
      consumed.add(param);
    }
  });

  // 2) Append any remaining keys as query parameters
  const entries = Object.entries(propsParams).filter(
    ([key, value]) => !consumed.has(key) && value !== null && value !== undefined,
  );

  if (entries.length > 0) {
    const [base, existingQuery = ""] = newUrl.split("?");
    const searchParams = new URLSearchParams(existingQuery);

    entries.forEach(([key, value]) => {
      // Normalize booleans/numbers and ensure proper encoding via URLSearchParams
      searchParams.set(key, String(value));
    });

    const qs = searchParams.toString();
    newUrl = qs ? `${base}?${qs}` : base;
  }

  return newUrl;
};

export enum Route {
  // Example
  Example = "/examples",
  ExampleCreate = "/examples/create",
  ExampleDetail = "/examples/:id",
  ExampleEdit = "/examples/:id/edit",

  // Companies
  Companies = "/tax-monitoring/subsidiary-companies/companies",
  CompaniesCreate = "/tax-monitoring/subsidiary-companies/companies/create",
  CompaniesDetail = "/tax-monitoring/subsidiary-companies/companies/:id",
  CompaniesEdit = "/tax-monitoring/subsidiary-companies/companies/:id/edit",

  // COA (Chart of Accounts)
  Coa = "/tax-monitoring/master-data/coa",
  CoaCreate = "/tax-monitoring/master-data/coa/create",
  CoaDetail = "/tax-monitoring/master-data/coa/:id",
  CoaEdit = "/tax-monitoring/master-data/coa/:id/edit",

  // Supplier
  Supplier = "/tax-monitoring/master-data/supplier",
  SupplierCreate = "/tax-monitoring/master-data/supplier/create",
  SupplierDetail = "/tax-monitoring/master-data/supplier/:id",
  SupplierEdit = "/tax-monitoring/master-data/supplier/:id/edit",

  // Pajak Daerah (Regional Tax) - Legacy processing pages
  PajakDaerah = "/tax-monitoring/tax-processing/pajak-daerah",
  PajakDaerahApproval = "/tax-monitoring/tax-processing/pajak-daerah/approval",
  PajakDaerahOutstanding = "/tax-monitoring/tax-processing/pajak-daerah/outstanding",
  PajakDaerahSspd = "/tax-monitoring/tax-processing/pajak-daerah/sspd",
  PajakDaerahDetail = "/tax-monitoring/tax-processing/pajak-daerah/:id",
  PajakDaerahEdit = "/tax-monitoring/tax-processing/pajak-daerah/:id/edit",

  // SSPD (Surat Setoran Pajak Daerah)
  Sspd = "/master-data/sspd",
  SspdCreate = "/master-data/sspd/create",
  SspdDetail = "/master-data/sspd/:id",
  SspdEdit = "/master-data/sspd/:id/edit",

  // FP NON 3M Reports (matching legacy routes exactly)
  ReportFp = "/tax-monitoring/reports/report-fp",
  OutstandingFp = "/tax-monitoring/reports/outstanding-fp",
  WaitingApprovalFpNon3m = "/tax-monitoring/reports/waiting-approval-fp-non-3m",

  // Entertain Reports
  ReportEntertain = "/tax-monitoring/reports/report-entertain",

  // Promotion Reports
  ReportsPromotion = "/tax-monitoring/reports/promotion",
  ApprovalPromotion = "/tax-monitoring/approval/promotion",

  // Pajak Daerah Reports
  PajakDaerahReports = "/tax-monitoring/reports/pajak-daerah",

  // CSR Reports
  CsrReports = "/tax-monitoring/reports/csr",
  CsrOutstanding = "/tax-monitoring/reports/csr/outstanding",
  CsrNde = "/tax-monitoring/reports/csr/nde",
  CsrApproval = "/tax-monitoring/reports/csr/approval",
  CsrEditByUpload = "/tax-monitoring/reports/csr/edit-by-upload",

  // Subsidiary Users
  SubsidiaryUser = "/tax-system/subsidiary-companies/user",
  SubsidiaryUserCreate = "/tax-system/subsidiary-companies/user/create",
  SubsidiaryUserDetail = "/tax-system/subsidiary-companies/user/:id",
  SubsidiaryUserEdit = "/tax-system/subsidiary-companies/user/:id/edit",

  //  Auth
  Login = "/auth/login",
  Logout = "/auth/logout",
  ForgotPassword = "/auth/forgot-password",
  ResetPassword = "/auth/forgot-password/reset",
  EmailActivation = "/auth/email-activation",
  EmailActivationCreatePassword = "/auth/email-activation/create-password",
  Unauthorized = "/401",

  // Invitation
  InvitationCustomerCreatePassword = "/invitation/customer/create-password/:token",
  InvitationFinanceCreatePassword = "/invitation/finance/create-password/:token",
  InvitationSales = "/invitation/sales/:token",
  InvitationSuccess = "/invitation/sales/:token/success",
  InvitationTokenExpired = "/invitation/token-expired",

  // Dashboard
  Dashboard = "/dashboard",
  Ticket = "/ticket",

  //Configure Landing Page
  ConfigureLandingPage = "/configure-landing-page",
  ConfigureLandingPageLogin = "/configure-landing-page/:id/login",
  ConfigureLandingPageFeatured = "/configure-landing-page/:id/featured",
  ConfigureLandingPageCommitmentList = "/configure-landing-page/:id/commitment",
  ConfigureLandingPageCommitmentCreate = "/configure-landing-page/:id/commitment/create",
  ConfigureLandingPageCommitmentEdit = "/configure-landing-page/:id/commitment/:itemId/edit",
  ConfigureLandingPageCommitmentDetail = "/configure-landing-page/:id/commitment/:itemId",

  // Profile
  Profile = "/profile",
  SupplierTaxSetupCreate = "/profile/supplier-tax-setup/create",
  SupplierTaxSetupEdit = "/profile/supplier-tax-setup/:id/edit",

  // Invoice
  InvoiceOi = "/invoice-oi",
  InvoiceOiDetail = "/invoice-oi/:id",
  InvoiceForm = "/invoice-oi/form",
  InvoiceFormParams = "/invoice-oi/form?",
  InvoiceFormEdit = "/invoice-oi/form?id=",
  PaidInvoice = "/invoice-oi/paid-invoice",
  RejectInvoice = "/invoice-oi/reject-invoice",
  FailedPostInvoice = "/invoice-oi/failed-post",
  PaidInvoiceOI = "/invoice-oi/paid-invoices",
  MailPaid = "/invoice-oi/mail-paid",
  MailPaidDetail = "/invoice-oi/mail-paid/:id",

  /* NOI External */
  InvoiceNOIExternal = "/invoice-noi-external",
  InvoiceNOIExternalDetail = "/invoice-noi-external/:type/:id",
  InvoiceNOIExternalForm = "/invoice-noi-external/:type/form",

  InvoiceNOIExternalCreditNote = "/invoice-noi-external/credit-note",
  InvoiceNOIExternalBeritaAcara = "/invoice-noi-external/berita-acara",

  /* NOI Internal */
  InvoiceNOIInternal = "/invoice-noi-internal",
  InvoiceNOIInternalPaidInvoices = "/invoice-noi-internal/paid-invoices",
  InvoiceNOIInternalForm = "/invoice-noi-internal/:type/form",
  InvoiceNOIInternalDetail = "/invoice-noi-internal/:type/:id",

  /* Extending Declaration */
  ExtendingDeclaration = "/invoice-noi-internal/extending-declarations",
  ExtendingDeclarationCreate = "/invoice-noi-internal/extending-declarations/create",
  ExtendingDeclarationDetail = "/invoice-noi-internal/extending-declarations/:id",

  // Config
  SupportingDocument = "/config/supporting-document",
  CurrencyRate = "/config/currency-rate",
  CurrencyRateCreate = "/config/currency-rate/create",
  Kurs = "/config/kurs",
  Pph = "/config/pph",
  TaxPeriod = "/config/tax-period",
  TaxIdentification = "/config/tax-identification",
  Skb = "/config/skb",

  // Master Data
  District = "/master-data/district",
  DistrictDetail = "/master-data/district/:id",
  Nitku = "/master-data/nitku",
  CreateNitku = "/master-data/nitku/create",
  EditNitku = "/master-data/nitku/edit",
  FiscalYears = "/master-data/fiscal-years",
  FiscalYearsCreate = "/master-data/fiscal-years/create",
  FiscalYearsDetail = "/master-data/fiscal-years/:id",
  FiscalYearsEdit = "/master-data/fiscal-years/:id/edit",
  FundedProgramOpex = "/master-data/funded-program",
  FundedProgramOpexDetail = "/master-data/funded-program/:id",
  MappingFundedProgramCapex = "/master-data/mapping-funded-program-capex",
  MappingFundedProgramCapexCreate = "/master-data/mapping-funded-program-capex/create",
  MappingFundedProgramCapexDetail = "/master-data/mapping-funded-program-capex/:id",
  MappingFundedProgramCapexEdit = "/master-data/mapping-funded-program-capex/:id/edit",
  MappingFundedProgramOpex = "/master-data/mapping-funded-program-opex",
  MappingFundedProgramOpexCreate = "/master-data/mapping-funded-program-opex/create",
  MappingFundedProgramOpexDetail = "/master-data/mapping-funded-program-opex/:id",
  MappingFundedProgramOpexEdit = "/master-data/mapping-funded-program-opex/:id/edit",
  AssetClass = "/master-data/pic-asset-class",
  AssetClassCreate = "master-data/pic-asset-class/create",
  AssetClassDetail = "/master-data/pic-asset-class/:id",
  AssetClassEdit = "/master-data/pic-asset-class/:id/edit",
  PicCommitmentItem = "/master-data/pic-commitment-item",
  PicCommitmentItemCreate = "master-data/pic-commitment-item/create",
  PicCommitmentItemDetail = "/master-data/pic-commitment-item/:id",
  PicCommitmentItemEdit = "/master-data/pic-commitment-item/:id/edit",
  Catalog = "/master-data/catalog",
  CatalogCreate = "/master-data/catalog/create",
  CatalogDetail = "/master-data/catalog/:id",
  CatalogEdit = "/master-data/catalog/:id/edit",

  // IAM
  Role = "/iam/role",
  RoleDetail = "/iam/role/:id",
  User = "/iam/user",
  UserCreate = "/iam/user/create",
  UserDetail = "/iam/user/:id",
  UserEdit = "/iam/user/:id/edit",
  Group = "/iam/group",
  GroupCreate = "/iam/group/create",
  GroupEdit = "/iam/group/:id/edit",
  GroupDetail = "/iam/group/:id",
  Employee = "/iam/employee",
  EmployeeCreate = "/iam/employee/create",
  EmployeeEdit = "/iam/employee/:id/edit",
  EmployeeDetail = "/iam/employee/:id",
  UserMapping = "/iam/user-mapping",
  UserMappingDetail = "/iam/user-mapping/:id",
  UserMappingCreate = "/iam/user-mapping/:id/create",
  UserMappingEdit = "/iam/user-mapping/:id/edit",

  // General Supplier Management
  SupplierManagement = "/supplier",
  SupplierManagementDetail = "/supplier/:id",
  GroupSupplier = "/supplier/group-supplier",
  GroupSupplierDetail = "/supplier/group-supplier/:id",
  GroupSupplierSetup = "/supplier/group-supplier/:groupSupplierId/setup/:supplierId",
  TaxSetup = "/supplier/tax-setup",
  TaxSetupCreate = "/supplier/tax-setup/create",
  TaxSetupEdit = "/supplier/tax-setup/:id/edit",
  TaxSetupDetail = "/supplier/tax-setup/:id",
  SuppleFinanceInvites = "/supplier/finance",
  SupplierContract = "/supplier/contract",

  // TAX
  Ppn = "/tax/ppn",
  WithholdingTax = "/tax/withholding-tax",
  Prepopulate = "/tax/prepopulate",

  // Receipt Invoice
  ReceiptInvoice = "/receipt-invoice",
  ReceiptInvoiceDetail = "/receipt-invoice/:id",
  ReceiptInvoiceCreate = "/receipt-invoice/create",

  // Notification
  Notification = "/notification",

  // Notification
  Queue = "/queue",

  // TWM
  Twm = "/twm/twm",
  TwmProblem = "/twm/twm-problem",
  TwmAccess = "/twm/twm-access",

  // WorkFlow
  Workflow = "/config/workflow",
  WorkflowCreate = "/config/workflow/create",
  WorkflowEdit = "/config/workflow/:id/edit",
  WorkflowDetail = "/config/workflow/:id",

  // Activity
  Activity = "/config/activity",
  ActivityCreate = "/config/activity/create",
  ActivityEdit = "/config/activity/:id/edit",
  ActivityDetail = "/config/activity/:id",

  // OTC
  OtcBeritaAcara = "/otc/berita-acara",
  OtcBeritaAcaraDetail = "/otc/berita-acara/:id",
  OtcBeritaAcaraCreate = "/otc/berita-acara/create",
  OtcBeritaAcaraEdit = "/otc/berita-acara/:id/edit",
  OtcMonitoringBeritaAcara = "/otc/berita-acara/monitoring-berita-acara",

  OtcBeritaAcaraScrapSaleBaps = "/otc/berita-acara-scrap-sale-baps",
  OtcBeritaAcaraScrapSaleDetailBaps = "/otc/berita-acara-scrap-sale-baps/:id",
  OtcBeritaAcaraScrapSaleCreateBaps = "/otc/berita-acara-scrap-sale-baps/create",
  OtcBeritaAcaraScrapSaleEditBaps = "/otc/berita-acara-scrap-sale-baps/:id/edit",

  OtcBeritaAcaraScrapSaleBass = "/otc/berita-acara-scrap-sale-bass",
  OtcBeritaAcaraScrapSaleDetailBass = "/otc/berita-acara-scrap-sale-bass/:id",
  OtcBeritaAcaraScrapSaleCreateBass = "/otc/berita-acara-scrap-sale-bass/create",
  OtcBeritaAcaraScrapSaleEditBass = "/otc/berita-acara-scrap-sale-bass/:id/edit",

  OtcMonitoringProgressClaim = "/otc/monitoring-progress-claim",
  OtcMonitoringCreateProgressClaim = "/otc/monitoring-progress-claim/create",
  OtcMonitoringDetailProgressClaim = "/otc/monitoring-progress-claim/:id",
  OtcMonitoringEditProgressClaim = "/otc/monitoring-progress-claim/:id/edit",

  // OTC Master Data
  OtcMasterCustomer = "/otc/master/customer",
  OtcMasterCustomerDetail = "/otc/master/customer/:id",
  OtcMasterCustomerUser = "/otc/master/customer/:id/user",
  OtcMasterCustomerUserCreate = "/otc/master/customer/:id/user/create",
  OtcMasterCustomerUserDetail = "/otc/master/customer/:id/user/:userid",
  OtcMasterCustomerUserEdit = "/otc/master/customer/:id/user/:userid/edit",
  OtcMasterCustomerNitku = "/otc/master/customer/:id/nitku",

  OtcMasterBackchargeType = "/otc/master/back-charge-type",
  OtcMasterBackchargeTypeDetail = "/otc/master/back-charge-type/:id",
  OtcMasterBackchargeTypeCreate = "/otc/master/back-charge-type/create",
  OtcMasterBackchargeTypeEdit = "/otc/master/back-charge-type/:id/edit",

  OtcMasterCustomerGrouping = "/otc/master/customer-grouping",
  OtcMasterCustomerGroupingDetail = "/otc/master/customer-grouping/:id",
  OtcMasterCustomerGroupingCreate = "/otc/master/customer-grouping/create",
  OtcMasterCustomerGroupingEdit = "/otc/master/customer-grouping/:id/edit",

  OtcMasterMaterial = "/otc/master/material",
  OtcMasterMaterialDetail = "/otc/master/material/:id",
  OtcMasterMaterialEdit = "/otc/master/material/:id/edit",
  OtcMasterMaterialCreate = "/otc/master/material/create",

  OtcMasterFacturCode = "/otc/master/facture-code",
  OtcMasterFacturCodeDetail = "/otc/master/facture-code/:id",
  OtcMasterFacturCodeCreate = "/otc/master/facture-code/create",
  OtcMasterFacturCodeEdit = "/otc/master/facture-code/:id/edit",

  OtcMasterNomorFaktur = "/otc/master/tax-config",
  OtcMasterNomorFakturDetail = "/otc/master/tax-config/:id",
  OtcMasterNomorFakturCreate = "/otc/master/tax-config/create",
  OtcMasterNomorFakturEdit = "/otc/master/tax-config/:id/edit",

  OtcMasterKeteranganTambahan = "/otc/master/keterangan-tambahan",
  OtcMasterKeteranganTambahanDetail = "/otc/master/keterangan-tambahan/:id",
  OtcMasterKeteranganTambahanEdit = "/otc/master/keterangan-tambahan/:id/edit",
  OtcMasterKeteranganTambahanCreate = "/otc/master/keterangan-tambahan/create",

  OtcMasterWorkflowExternal = "/otc/master/workflow-external",
  OtcMasterWorkflowExternalDetail = "/otc/master/workflow-external/:id",
  OtcMasterWorkflowExternalCreate = "/otc/master/workflow-external/create",
  OtcMasterWorkflowExternalEdit = "/otc/master/workflow-external/:id/edit",

  OtcMasterSatuan = "/otc/master/satuan",
  OtcMasterSatuanDetail = "/otc/master/satuan/:id",
  OtcMasterSatuanCreate = "/otc/master/satuan/create",
  OtcMasterSatuanEdit = "/otc/master/satuan/:id/edit",

  // OTC Invoice
  OtcInvoice = "/otc/invoice",
  OtcInvoiceDetail = "/otc/invoice/:id",
  OtcInvoiceEdit = "/otc/invoice/:id/edit",
  OtcInvoicePendingTask = "/otc/invoice/pending-task",
  OtcInvoiceBu = "/otc/invoice-wht",
  OtcInvoiceBuCreate = "/otc/invoice-wht/create",

  // OTC Invoice WHT
  OtcInvoiceWht = "/otc/invoice-wht",
  OtcInvoiceWhtDetail = "/otc/invoice-wht/:id",
  OtcInvoiceWhtCreate = "/otc/invoice-wht/create",
  OtcInvoiceWhtEdit = "/otc/invoice-wht/:id/edit",

  // OTC Invoice WHT Allocation
  OtcInvoiceWhtAllocation = "/otc/invoice-wht/:id/items",

  // TAX CONFIG OTC
  OtcTaxConfig = "/otc/master/tax-config?tab=tax-config",
  OtcTaxConfigDetail = "/otc/master/tax-config/tax-configs/:id",
  OtcTaxConfigCreate = "/otc/master/tax-config/tax-configs/create",
  OtcTaxConfigEdit = "/otc/master/tax-config/tax-configs/:id/edit",

  // Otc PIC DJP Config
  OtcMasterPicDjpConfig = "/otc/master/tax-config?tab=pic-djp-config",
  OtcMasterPicDjpConfigDetail = "/otc/master/tax-config/pic-djp-config/:id",
  OtcMasterPicDjpConfigCreate = "/otc/master/tax-config/pic-djp-config/create",
  OtcMasterPicDjpConfigEdit = "/otc/master/tax-config/pic-djp-config/:id/edit",

  // Otc Tax Config Facture Code
  OtcTaxConfigFactureCode = "/otc/master/tax-config?tab=facture-code",
  OtcTaxConfigFactureCodeDetail = "/otc/master/tax-config/facture-code/:id",
  OtcTaxConfigFactureCodeCreate = "/otc/master/tax-config/facture-code/create",
  OtcTaxConfigFactureCodeEdit = "/otc/master/tax-config/facture-code/:id/edit",

  // Payment Accelerate
  PaymentAccelerate = "/payment/accelerate",
  PaymentAccelerateCreate = "/payment/accelerate/create",
  PaymentAccelerateEdit = "/payment/accelerate/:id/edit",
  PaymentAccelerateDetail = "/payment/accelerate/:id",

  //Budget Plan Opex
  BudgetPlanOpex = "/opex/budget-plan",
  BudgetPlanOpexCreate = "/opex/budget-plan/create",
  BudgetPlanOpexDetail = "/opex/budget-plan/:id",
  BudgetPlanOpexEdit = "/opex/budget-plan/:id/edit",

  //Summary Opex
  SummaryOpex = "/opex/summary",
  SummaryOpexDetail = "/opex/summary/:summaryId",
  SummaryOpexCreate = "/opex/summary/create",
  SummaryOpexDetailDepartment = "/opex/summary/:summaryId/:departmentId",

  //Summary Data Opex
  SummarDatayOpex = "/opex/summary-data",
  SummarDatayOpexDetail = "/opex/summary-data/:summaryId",

  //Summary Data Capex
  SummaryDataCapex = "/capex/summary-data",
  SummaryDataCapexDetail = "/capex/summary-data/:summaryId",

  //Summary Capex
  SummaryCapex = "/capex/summary",
  SummaryCapexDetail = "/capex/summary/:summaryId",
  SummaryCapexCreate = "/capex/summary/create",
  SummaryCapexDetailDepartment = "/capex/summary/:summaryId/:departmentId",

  //Budget Plan Capex
  BudgetPlanCapex = "/capex/budget-plan",
  BudgetPlanCapexCreate = "/capex/budget-plan/create",
  BudgetPlanCapexDetail = "/capex/budget-plan/:id",
  BudgetPlanCapexEdit = "/capex/budget-plan/:id/edit",

  //Reclass
  ReclassOpex = "/supplementary-opex/reclass",
  ReclassCreateOpex = "/supplementary-opex/reclass/create",
  ReclassDetailOpex = "/supplementary-opex/reclass/:id",
  ReclassEditOpex = "/supplementary-opex/reclass/:id/edit",

  ReclassCapex = "/supplementary-capex/reclass",
  ReclassCreateCapex = "/supplementary-capex/reclass/create",
  ReclassDetailCapex = "/supplementary-capex/reclass/:id",
  ReclassEditCapex = "/supplementary-capex/reclass/:id/edit",

  // IBS
  Ibs = "/ibs",
  IbsLogs = "/ibs/logs",

  // NEW IBS
  NewIbs = "/new-ibs",
  NewIbsDetail = "/new-ibs/:id",
  NewIbsSubParticular = "/new-ibs/:id/particulars/:subParticularId",
  NewIbsDetailSubParticular = "/new-ibs/:id/particulars/:subParticularId/ibs/:ibsId",

  // WBS
  WbsList = "/wbs",
  WbsDetail = "/wbs/:id",
  WbsCreate = "/wbs/create",
  WbsEdit = "/wbs/:id/edit",

  // Opex Unbudget
  UnbudgetOpex = "/supplementary-opex/unbudget",
  UnbudgetCreateOpex = "/supplementary-opex/unbudget/create",
  UnbudgetDetailOpex = "/supplementary-opex/unbudget/:id",
  UnbudgetEditOpex = "/supplementary-opex/unbudget/:id/edit",
  // Opex Overbudget
  OpexOverbudgetList = "/supplementary-opex/overbudget",
  OpexOverbudgetDetail = "/supplementary-opex/overbudget/:id",
  OpexOverbudgetCreate = "/supplementary-opex/overbudget/create",
  OpexOverbudgetEdit = "/supplementary-opex/overbudget/:id/edit",

  // Opex Unbudget
  UnbudgetCapex = "/supplementary-capex/unbudget",
  UnbudgetCreateCapex = "/supplementary-capex/unbudget/create",
  UnbudgetDetailCapex = "/supplementary-capex/unbudget/:id",
  UnbudgetEditCapex = "/supplementary-capex/unbudget/:id/edit",

  // Opex Upload Budgeting
  UploadBudgetingAcsDetail = "/upload-budgeting/acs/:id",

  // CapEx Overbudget
  CapexOverbudgetList = "/supplementary-capex/overbudget",
  CapexOverbudgetDetail = "/supplementary-capex/overbudget/:id",
  CapexOverbudgetCreate = "/supplementary-capex/overbudget/create",
  CapexOverbudgetEdit = "/supplementary-capex/overbudget/:id/edit",

  // Filling System  All Document
  PamaFillingSystemAllDocument = "/document-management/all-document",
  PamaFillingSystemAllDocumentBulkSearch = "/document-management/all-document/bulk-search",
  PamaFillingSystemAllDocumentDetail = "/document-management/all-document/:id",

  // Filing Document Pending
  FilingDocumentPending = "/document-management/pending-task",
  FilingDocumentPendingBulkSearch = "/document-management/pending-task/bulk-search",

  // Filing Document Filling
  FillingDocumentFilling = "/document-management/filling",
  FillingDocumentFillingDetail = "/document-management/filling/:id",
  FillingDocumentFillingCreate = "/document-management/filling/create",
  FillingDocumentFillingEdit = "/document-management/filling/:id/edit",

  // Warehouse Management
  WarehouseManagementRack = "/warehouse-management/rack",
  WarehouseManagementRackCreate = "/warehouse-management/rack/create",
  WarehouseManagementRackDetail = "/warehouse-management/rack/:id",
  WarehouseManagementRackEdit = "/warehouse-management/rack/:id/edit",

  // Warehouse Management Rack Overview
  WarehouseManagementRackOverview = "/warehouse-management/rack-overview",

  //Package
  Package = "/package",

  // Loan Application
  LoanApplication = "/peminjaman/loan-applications",
  LoanApplicationCreate = "/peminjaman/loan-applications/create",
  LoanApplicationDetail = "/peminjaman/loan-applications/:id",
  LoanApplicationEdit = "/peminjaman/loan-applications/:id/edit",
  LoanedApplication = "/peminjaman/loaned-applications",
  LoanedApplicationDetail = "/peminjaman/loaned-applications/:id",
  LoanApplicationApproval = "/peminjaman/approval-submit",
  LoanApplicationApprovalDetail = "/peminjaman/approval-submit/:id",
  LoanApplicationApprovalSend = "/peminjaman/approval-send",
  LoanApplicationApprovalSendDetail = "/peminjaman/approval-send/:id",
  LoanApplicationApprovalReturn = "/peminjaman/approval-return",
  LoanApplicationApprovalReturnDetail = "/peminjaman/approval-return/:id",
  LoanApplicationBorrowed = "/peminjaman/borrowed-applications",
  LoanApplicationBorrowedDetail = "/peminjaman/borrowed-applications/:id",
  LoanApplicationAcceptedRequest = "/peminjaman/accepted-request",
  LoanApplicationAcceptedRequestDetail = "/peminjaman/accepted-request/:id",
  LoanApplicationPendingRequest = "/peminjaman/pending-request",
  LoanApplicationPendingRequestDetail = "/peminjaman/pending-request/:id",

  // Approval Return
  ApprovalReturn = "/peminjaman/approval-return",
  ApprovalReturnDetail = "/peminjaman/approval-return/:id",

  // Borrowed Application
  BorrowedApplication = "/peminjaman/borrowed-applications",
  BorrowedApplicationDetail = "/peminjaman/borrowed-applications/:id",

  // Tax System Master Data Taxes
  TaxSystemMasterDataTaxes = "/tax-system/master-data/taxes",
  TaxSystemMasterDataTaxesCreate = "/tax-system/master-data/taxes/create",
  TaxSystemMasterDataTaxesDetail = "/tax-system/master-data/taxes/:id",
  TaxSystemMasterDataTaxesEdit = "/tax-system/master-data/taxes/:id/edit",

  // Tax System Master Data Document Profile Manifest
  DocumentProfileManifest = "/tax-system/master-data/document-profile-manifest",
  DocumentProfileManifestCreate = "/tax-system/master-data/document-profile-manifest/create",
  DocumentProfileManifestDetail = "/tax-system/master-data/document-profile-manifest/:id",
  DocumentProfileManifestEdit = "/tax-system/master-data/document-profile-manifest/:id/edit",
  // Tax System Master Data Odners
  TaxSystemMasterDataOdners = "/tax-system/master-data/odners",
  TaxSystemMasterDataOdnersCreate = "/tax-system/master-data/odners/create",
  TaxSystemMasterDataOdnersDetail = "/tax-system/master-data/odners/:id",
  TaxSystemMasterDataOdnersEdit = "/tax-system/master-data/odners/:id/edit",
  // Tax System Master Data Letters
  TaxSystemMasterDataLetters = "/tax-system/master-data/letters",
  TaxSystemMasterDataLettersCreate = "/tax-system/master-data/letters/create",
  TaxSystemMasterDataLettersDetail = "/tax-system/master-data/letters/:id",
  TaxSystemMasterDataLettersEdit = "/tax-system/master-data/letters/:id/edit",
  // Tax System Master Data Sections
  TaxSystemMasterDataSections = "/tax-system/master-data/sections",
  TaxSystemMasterDataSectionsCreate = "/tax-system/master-data/sections/create",
  TaxSystemMasterDataSectionsDetail = "/tax-system/master-data/sections/:id",
  TaxSystemMasterDataSectionsEdit = "/tax-system/master-data/sections/:id/edit",
  // Tax System Master Data Category Issues
  TaxSystemMasterDataCategoryIssues = "/tax-system/master-data/category-issues",
  TaxSystemMasterDataCategoryIssuesCreate = "/tax-system/master-data/category-issues/create",
  TaxSystemMasterDataCategoryIssuesDetail = "/tax-system/master-data/category-issues/:id",
  TaxSystemMasterDataCategoryIssuesEdit = "/tax-system/master-data/category-issues/:id/edit",
  // Tax System Master Data Affco
  TaxSystemMasterDataAffco = "/tax-system/master-data/affco",
  TaxSystemMasterDataAffcoCreate = "/tax-system/master-data/affco/create",
  TaxSystemMasterDataAffcoDetail = "/tax-system/master-data/affco/:id",
  TaxSystemMasterDataAffcoEdit = "/tax-system/master-data/affco/:id/edit",
  // Tax System Master Data Holidays
  TaxSystemMasterDataHolidays = "/tax-system/master-data/holidays",
  TaxSystemMasterDataHolidaysCreate = "/tax-system/master-data/holidays/create",
  TaxSystemMasterDataHolidaysDetail = "/tax-system/master-data/holidays/:id",
  TaxSystemMasterDataHolidaysEdit = "/tax-system/master-data/holidays/:id/edit",

  // Tax Monitoring SSPD
  TaskListOutstandingSspd = "/tax-monitoring/task-list-outstanding/sspd",

  // Tax Monitoring Pajak Dearah
  ApprovalPajakDaerah = "/tax-monitoring/approval/pajak-daerah",

  // Tax Monitoring FP NON 3M Terkreditkan
  TaskListOutstandingFpNon3m = "/tax-monitoring/task-list-outstanding/fp-non-3m",

  // Tax Monitoring Entertain
  TaskListOutstandingEntertain = "/tax-monitoring/task-list-outstanding/entertain",
  TaskListOutstandingEntertainEdit = "/tax-monitoring/task-list-outstanding/entertain/:id/edit",
  TaskListOutstandingEntertainUpload = "/tax-monitoring/task-list-outstanding/entertain/upload",
  ApprovalEntertain = "/tax-monitoring/approval/entertain",

  // Tax Monitoring Promotion
  TaskListOutstandingPromotion = "/tax-monitoring/task-list-outstanding/promotion",
  TaskListOutstandingPromotionEdit = "/tax-monitoring/task-list-outstanding/promotion/:id/edit",
  TaskListOutstandingPromotionUpload = "/tax-monitoring/task-list-outstanding/promotion/upload",

  // Tax Monitoring Csr
  TaskListOutstandingCsr = "/tax-monitoring/task-list-outstanding/csr",
  TaskListOutstandingCsrEdit = "/tax-monitoring/task-list-outstanding/csr/:id/edit",
  TaskListOutstandingCsrUpload = "/tax-monitoring/task-list-outstanding/csr/upload",
  ApprovalCsr = "/tax-monitoring/approval/csr",

  // Tax Monitoring Extracompt
  TaskListOutstandingExtracompt = "/tax-monitoring/task-list-outstanding/extracompt",
  TaxMonitoringReportExtracompt = "/tax-monitoring/reports/extracomp",

  // Tax System Tax Compliance Reports PPh Unifikasi
  TaxSystemTaxComplianceReports = "/tax-system/tax-compliance-reports",
  TaxSystemPphUnifikasi = "/tax-system/tax-compliance-reports/pph-unifikasi",
  TaxSystemPphUnifikasiCreate = "/tax-system/tax-compliance-reports/pph-unifikasi/create",
  TaxSystemPphUnifikasiDetail = "/tax-system/tax-compliance-reports/pph-unifikasi/:id",
  TaxSystemPphUnifikasiPerbaikan = "/tax-system/tax-compliance-reports/pph-unifikasi/:id/perbaikan",

  // Tax System Tax Compliance Reports PPh 21/26
  TaxSystemPph2126 = "/tax-system/tax-compliance-reports/pph-2126",
  TaxSystemPph2126Create = "/tax-system/tax-compliance-reports/pph-2126/create",
  TaxSystemPph2126Detail = "/tax-system/tax-compliance-reports/pph-2126/:id",
  TaxSystemPph2126Edit = "/tax-system/tax-compliance-reports/pph-2126/:id/edit",

  // Tax System Tax Compliance Reports PPh Badan
  TaxSystemPphBadan = "/tax-system/tax-compliance-reports/pph-badan",
  TaxSystemPphBadanCreate = "/tax-system/tax-compliance-reports/pph-badan/create",
  TaxSystemPphBadanDetail = "/tax-system/tax-compliance-reports/pph-badan/:id",
  TaxSystemPphBadanPerbaikan = "/tax-system/tax-compliance-reports/pph-badan/:id/perbaikan",

  TaxSystemPpn = "/tax-system/tax-compliance-reports/ppn",
  TaxSystemPpnCreate = "/tax-system/tax-compliance-reports/ppn/create",
  TaxSystemPpnDetail = "/tax-system/tax-compliance-reports/ppn/:id",
  TaxSystemPpnPerbaikan = "/tax-system/tax-compliance-reports/ppn/:id/perbaikan",

  TaxSystemSuratMasukList = "/tax-system/surat-masuk",
  TaxSystemSuratMasukCreate = "/tax-system/surat-masuk/create",
  TaxSystemSuratMasukDetail = "/tax-system/surat-masuk/:id",
  TaxSystemSuratMasukAllocate = "/tax-system/surat-masuk/:id/allocate",
  TaxSystemSuratMasukEdit = "/tax-system/surat-masuk/:id/edit",
  TaxSystemSuratMasukArchive = "/tax-system/surat-masuk/:id/archive",

  // Tax System Tax Compliance Reports Pajak Daerah
  TaxSystemPajakDaerah = "/tax-system/tax-compliance-reports/pajak-daerah",
  TaxSystemPajakDaerahDetail = "/tax-system/tax-compliance-reports/pajak-daerah/:id",
  TaxSystemPajakDaerahSpopdDetail = "/tax-system/tax-compliance-reports/pajak-daerah/:taxTransactionId/spopd/:taxTransactionDetailId",
  TaxSystemPajakDaerahSpopdEdit = "/tax-system/tax-compliance-reports/pajak-daerah/:taxTransactionId/spopd/:taxTransactionDetailId/edit/:spopdId",

  // Tax System Daftar Tindak Lanjut
  TaxSystemDaftarTindakLanjut = "/tax-system/kms/daftar-tindak-lanjut",
  TaxSystemDaftarTindakLanjutDetail = "/tax-system/kms/daftar-tindak-lanjut/:id",

  // Tax System KMS Surat Keluar
  TaxSystemKmsSuratKeluar = "/tax-system/kms/surat-keluar",
  TaxSystemKmsSuratKeluarDetail = "/tax-system/kms/surat-keluar/:id",

  // Tax System KMS Surat Masuk
  TaxSystemKmsSuratMasuk = "/tax-system/kms/surat-masuk",
  TaxSystemKmsSuratMasukDetail = "/tax-system/kms/surat-masuk/:id",

  // Tax System Surat Keluar
  TaxSystemSuratKeluar = "/tax-system/surat-keluar",
  TaxSystemSuratKeluarCreate = "/tax-system/surat-keluar/create",
  TaxSystemSuratKeluarDetail = "/tax-system/surat-keluar/:id",
  TaxSystemSuratKeluarEdit = "/tax-system/surat-keluar/:id/edit",
  TaxSystemSuratKeluarSend = "/tax-system/surat-keluar/:id/send",
  TaxSystemOutstandingLetterDetail = "/tax-system/surat-keluar/outstanding-letters/:id",
  TaxSystemOutstandingLetterCreate = "/tax-system/surat-keluar/outstanding-letters/:id/create",

  // Tax System Tax Case Study
  TaxSystemTaxCaseStudy = "/tax-system/tax-case-studies",
  TaxSystemTaxCaseStudyCreate = "/tax-system/tax-case-studies/create",
  TaxSystemTaxCaseStudyDetail = "/tax-system/tax-case-studies/:id",
  TaxSystemTaxCaseStudyEdit = "/tax-system/tax-case-studies/:id/edit",
  TaxSystemTaxCaseStudyAssessment = "/tax-system/tax-case-studies/:id/assessment",
  TaxSystemTaxCaseStudyEditAssessment = "/tax-system/tax-case-studies/:id/edit-assessment",

  // Tax System Case Studies (Studi Kasus - Read Only)
  TaxSystemCaseStudies = "/tax-system/case-studies",
  TaxSystemCaseStudiesDetail = "/tax-system/case-studies/:id",

  // Tax System Filling Odner
  TaxSystemFillingOdner = "/tax-system/filling-odner",
  TaxSystemFillingOdnerDetail = "/tax-system/filling-odner/:id",

  // Tax System Tax Profile Manifest
  TaxSystemTaxProfileManifest = "/tax-system/tax-profile-manifest",
  TaxSystemTaxProfileManifestEdit = "/tax-system/tax-profile-manifest/edit",
  TaxSystemTaxProfileManifestNitkuCreate = "/tax-system/tax-profile-manifest/nitku/create",
  TaxSystemTaxProfileManifestNitkuEdit = "/tax-system/tax-profile-manifest/nitku/:id/edit",
  TaxSystemTaxProfileManifestNitkuDetail = "/tax-system/tax-profile-manifest/nitku/:id",

  // Tax System Tax Profile Manifest Documents
  TaxSystemTaxProfileManifestDocumentCreate = "/tax-system/tax-profile-manifest/documents/create",
  TaxSystemTaxProfileManifestDocumentTurunanCreate = "/tax-system/tax-profile-manifest/documents/:id/create-turunan",
  TaxSystemTaxProfileManifestDocumentEdit = "/tax-system/tax-profile-manifest/documents/:id/edit",
  TaxSystemTaxProfileManifestDocumentDetail = "/tax-system/tax-profile-manifest/documents/:id",
  TaxSystemTaxProfileManifestDocumentHistory = "/tax-system/tax-profile-manifest/documents/:id/history",

  // Tax Monitoring NDE Entertain
  TaxMonitoringNdeEntertain = "/tax-monitoring/nde/entertain",
  TaxMonitoringNdePromotion = "/tax-monitoring/nde/promotion",
  TaxMonitoringNdeCsr = "/tax-monitoring/nde/csr",

  // Tax Monitoring BuildingMaintenance
  TaxMonitoringBuildingMaintenanceOutstanding = "/tax-monitoring/task-list-outstanding/building-maintenance",
  TaxMonitoringBuildingMaintenanceReports = "/tax-monitoring/reports/building-maintenance",
  TaxMonitoringBuildingMaintenanceReportsParams = "/tax-monitoring/reports/building-maintenance/building-params",
  TaxMonitoringBuildingMaintenanceReportsParamsCreate = "/tax-monitoring/reports/building-maintenance/building-params/create",

  // Tax Monitoring Projects Monitoring
  ProjectsMonitoring = "/tax-monitoring/project-monitoring/projects",
  ProjectsMonitoringExecutiveSummary = "/tax-monitoring/project-monitoring/executive-summary",
  ProjectsMonitoringCreate = "/tax-monitoring/project-monitoring/projects/create",
  ProjectsMonitoringDetail = "/tax-monitoring/project-monitoring/projects/:id",
  ProjectsMonitoringEdit = "/tax-monitoring/project-monitoring/projects/:id/edit",
  ProjectsMonitoringAddCompanyGroup = "/tax-monitoring/project-monitoring/projects/:id/add-company-group",
  ProjectsMonitoringLastActivity = "/tax-monitoring/project-monitoring/projects/:id/last-activity",
  ProjectsMonitoringFinInformationLink = "/tax-monitoring/project-monitoring/projects/:id/count-fin-information-link",
  ProjectsMonitoringFinInformationDocument = "/tax-monitoring/project-monitoring/projects/:id/count-fin-information-document",
  ProjectsMonitoringFinInformationLinkList = "/tax-monitoring/project-monitoring/projects/:id/get-fin-information-link",
  ProjectsMonitoringFinInformationDocmentList = "/tax-monitoring/project-monitoring/projects/:id/get-fin-information-document",
  ProjectMonitoringPreliminaryDocumentCount = "/tax-monitoring/project-monitoring/projects/:id/count-preliminary-document",
  ProjectMonitoringPreliminaryDocumentList = "/tax-monitoring/project-monitoring/projects/:id/get-preliminary-document",
  ProjectMonitoringViewSummaryProgress = "/tax-monitoring/project-monitoring/projects/:id/view-summary-progress",
  ProjectMonitoringListSummaryProgress = "/tax-monitoring/project-monitoring/projects/:id/get-summary-progress",
  ProjectMonitoringCspaDocumentList = "/tax-monitoring/project-monitoring/projects/:id/get-cspa-documents",
  ProjectMonitoringClosingTransactionList = "/tax-monitoring/project-monitoring/projects/:id/get-closing-transaction-documents",
  ProjectMonitoringAppraisalDocumentList = "/tax-monitoring/project-monitoring/projects/:id/get-appraisal-documents",

  // Tax Monitoring Projects Monitoring Dashboard
  ProjectsMonitoringActivitiy = "/tax-monitoring/project-monitoring/projects/activity",
  ProjectsMonitoringMasterDatabase = "/tax-monitoring/project-monitoring/projects/master-database",
  ProjectsMonitoringManagementReview = "/tax-monitoring/project-monitoring/projects/:id/management-review",

  // Tax Monitoring Projects Monitoring Oustanding
  ProjectsMonitoringOutstandingProjects = "/tax-monitoring/project-monitoring/outstanding-projects",

  // Management Reports

  // Period Plans
  MRPeriodPlans = "/management-reports/configuration/period-plans",
  MRPeriodPlansCreate = "/management-reports/configuration/period-plans/create",
  MRPeriodPlansDetail = "/management-reports/configuration/period-plans/:id",
  MRPeriodPlansEdit = "/management-reports/configuration/period-plans/:id/edit",

  // Period Actuals
  MRPeriodActuals = "/management-reports/configuration/period-actuals",
  MRPeriodActualsCreate = "/management-reports/configuration/period-actuals/create",
  MRPeriodActualsDetail = "/management-reports/configuration/period-actuals/:id",
  MRPeriodActualsEdit = "/management-reports/configuration/period-actuals/:id/edit",

  // Entity Districts
  MREntityDistricts = "/management-reports/master-data/entity-districts",
  MREntityDistrictsCreate = "/management-reports/master-data/entity-districts/create",
  MREntityDistrictsDetail = "/management-reports/master-data/entity-districts/:id",
  MREntityDistrictsEdit = "/management-reports/master-data/entity-districts/:id/edit",

  // Descriptions
  MRDescriptions = "/management-reports/master-data/descriptions",
  MRDescriptionsCreate = "/management-reports/master-data/descriptions/create",
  MRDescriptionsDetail = "/management-reports/master-data/descriptions/:id",
  MRDescriptionsEdit = "/management-reports/master-data/descriptions/:id/edit",
  MRDescriptionsDependencies = "/management-reports/master-data/descriptions/:id/dependencies",

  // Data Integrities
  MRDataIntegrities = "/management-reports/data-monitoring/data-integrities",
  MRDataIntegritiesDetail = "/management-reports/data-monitoring/data-integrities/:year/:month/dashboards/:dashboardId",

  // Table Configs
  MRTableConfigs = "/management-reports/table-configs",
  MRTableConfigsDetail = "/management-reports/table-configs/:id",

  // Data Calculations
  MRDataCalculations = "/management-reports/data-calculations",
  MRDataCalculationsCreate = "/management-reports/data-calculations/create",
  MRDataCalculationsDetail = "/management-reports/data-calculations/:id",
  MRDataCalculationsEdit = "/management-reports/data-calculations/:id/edit",
  MRDataCalculationsDependencies = "/management-reports/data-calculations/:id/dependencies",

  // Data Transactions
  MRDataTransactions = "/management-reports/data-transactions",
  MRDataTransactionsCreate = "/management-reports/data-transactions/create",
  MRDataTransactionsDetail = "/management-reports/data-transactions/:id",
  MRDataTransactionsEdit = "/management-reports/data-transactions/:id/edit",
  MRDataTransactionsDependencies = "/management-reports/data-transactions/:id/dependencies",

  // Data Uploads
  MRDataUploads = "/management-reports/data-uploads",
  MRDataUploadsCreate = "/management-reports/data-uploads/create",
  MRDataUploadsDetail = "/management-reports/data-uploads/:id",

  // Review Summaries
  MRReviewSummaries = "/management-reports/review-summaries",
  MRReviewSummariesDetail = "/management-reports/review-summaries/:id",

  // Recalculations 2
  MRRecalculations = "/management-reports/recalculations",

  // Mapping Tables
  MRMappingTables = "/management-reports/data-monitoring/mapping-tables",
  MRMappingTablesCreate = "/management-reports/data-monitoring/mapping-tables/create",
  MRMappingTablesDetail = "/management-reports/data-monitoring/mapping-tables/:id",
  MRMappingTablesEdit = "/management-reports/data-monitoring/mapping-tables/:id/edit",

  // Log Activities
  MRLogActivities = "/management-reports/log-activities",
}

/**
 * List of routes that can be accessed without authentication.
 * These routes correspond to the pages in src/app/(unauthenticated).
 */
export const PUBLIC_ROUTES: string[] = [
  Route.Login,
  Route.ForgotPassword,
  Route.ResetPassword,
  Route.EmailActivation,
  Route.EmailActivationCreatePassword,
  Route.InvitationCustomerCreatePassword,
  Route.InvitationFinanceCreatePassword,
  Route.InvitationSales,
  Route.InvitationSuccess,
  Route.InvitationTokenExpired,
  Route.Unauthorized,
];

/**
 * List of routes that are public but should NOT redirect to dashboard if user is logged in.
 */
export const PUBLIC_ROUTES_NO_REDIRECT: string[] = [Route.Logout];

/**
 * Helper to check if a pathname is a public route.
 */
export const isPublicRoute = (
  pathname: string,
  options?: { excludeNoRedirect?: boolean },
): boolean => {
  const routes = options?.excludeNoRedirect
    ? PUBLIC_ROUTES
    : [...PUBLIC_ROUTES, ...PUBLIC_ROUTES_NO_REDIRECT];

  return routes.some((route) => {
    // Remove dynamic parameters for matching
    const baseRoute = route.split("/:")[0];
    return pathname === baseRoute || pathname.startsWith(`${baseRoute}/`);
  });
};
